import { useId, useState } from 'react';
import {
    VideoCameraIcon,
    ClipboardDocumentCheckIcon,
    ClockIcon,
    ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getActivityPanelTitle } from './calendarEvents';

const STAT_CONFIG = {
    classes: {
        icon: VideoCameraIcon,
        label: 'Clases programadas',
        modifier: 'cal-activity__stat--classes',
    },
    pending: {
        icon: ClipboardDocumentCheckIcon,
        label: 'Tareas por entregar',
        modifier: 'cal-activity__stat--pending',
    },
    upcoming: {
        icon: ClockIcon,
        label: 'Por activarse',
        modifier: 'cal-activity__stat--upcoming',
    },
};

const ActivityStat = ({ kind, value }) => {
    const config = STAT_CONFIG[kind];
    const Icon = config.icon;

    return (
        <div className={`cal-sidebar-row cal-activity__stat ${config.modifier}`}>
            <span className={`cal-sidebar-row__icon ${config.modifier}`} aria-hidden="true">
                <Icon className="cal-sidebar-row__icon-svg" />
            </span>
            <span className="cal-sidebar-row__label" title={config.label}>{config.label}</span>
            <span className="cal-sidebar-row__value">{value}</span>
            <span className="cal-sidebar-row__slot" aria-hidden="true" />
        </div>
    );
};

const TaskGroup = ({
    kind,
    items,
    dateKind = 'due',
    focusedEventId,
    onSelectEvent,
}) => {
    const [open, setOpen] = useState(false);
    const panelId = useId();
    const config = STAT_CONFIG[kind];
    const Icon = config.icon;
    const canOpen = items.length > 0;

    return (
        <div className={`cal-activity__group${open ? ' is-open' : ''}`}>
            <button
                type="button"
                disabled={!canOpen}
                aria-expanded={canOpen ? open : undefined}
                aria-controls={canOpen ? panelId : undefined}
                onClick={() => canOpen && setOpen((prev) => !prev)}
                className={`cal-sidebar-row cal-sidebar-row--button cal-activity__group-trigger ${config.modifier}`}
            >
                <span className={`cal-sidebar-row__icon ${config.modifier}`} aria-hidden="true">
                    <Icon className="cal-sidebar-row__icon-svg" />
                </span>
                <span className="cal-sidebar-row__label" title={config.label}>{config.label}</span>
                <span className="cal-sidebar-row__value">{items.length}</span>
                <span className="cal-sidebar-row__slot">
                    <ChevronDownIcon
                        className={`cal-sidebar-row__chevron${
                            canOpen ? '' : ' cal-sidebar-row__chevron--placeholder'
                        }${open ? ' is-open' : ''}`}
                        aria-hidden="true"
                    />
                </span>
            </button>

            <div
                id={panelId}
                className={`cal-activity__group-panel${
                    open && canOpen ? ' cal-activity__group-panel--open' : ''
                }`}
            >
                <ul className="cal-activity__task-list">
                    {items.map((event) => {
                        const focused = event.id === focusedEventId;
                        const when = dateKind === 'activation'
                            ? new Date(event.resource?.task?.activationDate || event.start)
                            : event.start;

                        return (
                            <li key={event.id}>
                                <button
                                    type="button"
                                    onClick={() => onSelectEvent(event)}
                                    className={`cal-activity__task${focused ? ' is-focused' : ''}`}
                                >
                                    <span className="cal-activity__task-title" title={event.title}>{event.title}</span>
                                    <time
                                        className="cal-activity__task-when"
                                        dateTime={when.toISOString()}
                                    >
                                        {format(when, 'd MMM · HH:mm', { locale: es })}
                                    </time>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

/**
 * Panel lateral: métricas de clases y tareas próximas visibles en el rango del calendario.
 */
export const CalendarActivityPanel = ({
    calendarView = 'week',
    summary,
    taskGroups = { pending: [], upcoming: [] },
    showUpcoming = false,
    canViewTasks,
    focusedEventId,
    onSelectTaskEvent,
}) => (
    <section className="cal-sidebar-panel cal-activity" aria-labelledby="cal-activity-title">
        <header className="cal-sidebar-panel__header">
            <h3 id="cal-activity-title" className="cal-sidebar-panel__title">
                {getActivityPanelTitle(calendarView)}
            </h3>
        </header>

        <div className="cal-sidebar-panel__list cal-activity__body">
            <ActivityStat kind="classes" value={summary.classes} />

            {canViewTasks && (
                <TaskGroup
                    kind="pending"
                    items={taskGroups.pending}
                    dateKind="due"
                    focusedEventId={focusedEventId}
                    onSelectEvent={onSelectTaskEvent}
                />
            )}

            {canViewTasks && showUpcoming && (
                <TaskGroup
                    kind="upcoming"
                    items={taskGroups.upcoming}
                    dateKind="activation"
                    focusedEventId={focusedEventId}
                    onSelectEvent={onSelectTaskEvent}
                />
            )}
        </div>
    </section>
);
